<template>
  <general-loader v-if="loading" loading/>
  <TabLayout v-else class="receipt-view" :tabs="tabs">
    <template v-slot:title>
      <artisio-breadcrumb :items="breadcrumbs"/>
    </template>
    <template v-slot:buttons>
      <b-button v-permission="'change_receipt'" variant="primary" @click="onCreateClick" class="mr-2">
        <font-awesome-icon icon="plus-circle" class="icon"/>
        {{ $t('Create Receipt') }}
      </b-button>
      <b-button v-permission="'change_inventoryitem'" variant="primary" @click="onInventoryCreateClick" class="mr-2">
        <font-awesome-icon icon="plus-circle" class="icon"/>
        {{ $t('Create Inventory Item') }}
      </b-button>
      <b-dropdown v-permission="'change_receipt'" right :text="$t('More')">
        <b-dropdown-item @click="onEditClick">
          {{ $t('Edit Receipt') }}
        </b-dropdown-item>
<!--        <b-dropdown-item v-permission="'export_receipt'" @click="onGeneratePdfClick">-->
<!--          {{ $t('Generate PDF') }}-->
<!--        </b-dropdown-item>-->
        <b-dropdown-item v-permission="'export_receipt'" @click="onQrCodeClick">
          {{ $t('View QR') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'export_receipt'" @click="onGeneratePreReceiptContractClick">
          {{ $t('View Pre-Consignment') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'export_receipt'" @click="onGenerateReceiptContractClick">
          {{ $t('View Consignment') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'export_receipt'" @click="onReceiptStickerClick">
          {{ $t('Receipt Sticker') }}
        </b-dropdown-item>
      </b-dropdown>
    </template>
    <inventory-modal/>
  </TabLayout>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import InventoryModal from "../../inventory/InventoryModal";
  import {SOURCE_RECEIPT_DASHBOARD, SOURCE_RECEIPT_VIEW} from "../../constants";
  import ArtisioBreadcrumb from "../../../core/components/artisio-breadcrumb/ArtisioBreadcrumb";
  import TabLayout from "../../../core/components/layout/DefaultLayout/TabLayout";
  import ReceiptService from "../ReceiptService";
  import EventBus from "../../../plugins/event-bus";
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";

  const {mapActions, mapState} = createNamespacedHelpers('receipts');
  const {mapActions: inventoryMapActions, mapState: inventoryMapState} = createNamespacedHelpers('inventory');
  const {mapState: globalMapState, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "ReceiptView",
    components: {GeneralLoader, TabLayout, ArtisioBreadcrumb, InventoryModal},
    data: () => {
      return {
        breadcrumbs: [],
      };
    },
    computed: {
      ...mapState({
        loading: state => state.view.loading,
        receipt: state => state.view.currentReceipt
      }),
      ...inventoryMapState({
        inventorySearchModel: state => state.tableData[state.tableSource] ? state.tableData[state.tableSource].searchModel : null
      }),
      ...globalMapState({
        enableNlRelations: state => state.enableNlRelations,
      }),
      tabs() {
        let data = [
          {title: 'Dashboard', icon: 'server', to: {name: 'receipt.dashboard'}},
          {title: 'Inventory', icon: 'boxes', to: {name: 'receipt.inventory'}, viewPermission: 'view_inventoryitem'},
          {title: 'Terms', icon: 'percentage', to: {name: 'receipt.terms'}},
          {title: this.$i18n.t('Vendor Charges'), icon: 'percentage', to: {name: 'receipt.vendorCharges'}},
          {title: 'Relations', icon: 'users', to: {name: 'receipt.relations'}},
          {title: 'Attachments', icon: 'copy', to: {name: 'receipt.attachments'}},
          {title: 'Return Notes', icon: 'copy', to: {name: 'receipt.returnNotes'}},
          {title: 'Contracts', icon: 'copy', to: {name: 'receipt.contracts'}},
          {title: 'Notes', icon: 'comment-alt', to: {name: 'receipt.notes'}},
          {title: 'Activities', icon: 'stream', to: {name: 'receipt.activities'}},
        ]

        return data
      }
    },
    watch: {
      async '$route.params.id'() {
        await this.getReceipt(this.$route.params.id);
        this.initBreadcrumbs();
      }
    },
    methods: {
      ...mapActions(['getReceipt', 'showModal', 'getModalObject', 'updateViewReceipt']),
      ...mapActionsGlobal([
        'showGeneralContractModal',
        'getGeneralContractModalData',
      ]),
      ...inventoryMapActions({
        showInventoryModal: 'showModal'
      }),
      onEditClick() {
        this.showModal({
          object: this.receipt,
          source: SOURCE_RECEIPT_VIEW
        });
        this.getModalObject({uuid: this.receipt.uuid, source: SOURCE_RECEIPT_DASHBOARD});
      },
      onCreateClick() {
        this.showModal();
      },
      onInventoryCreateClick() {
        this.showInventoryModal({
          object: {
            receipt: this.receipt,
            receipt_uuid: this.receipt.uuid
          }
        })
      },
      async onQrCodeClick() {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t("Receipt {receipt_no}", {receipt_no: this.receipt.receipt_no}),
        });
        let response = await QrCodeService.generateQrCode({
          model_type: 'receipt',
          model_uuid: this.receipt.uuid,
        })
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
          })
        }
      },
      async onGeneratePdfClick() {
        EventBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Receipt')});
        let response = await ReceiptService.getReceiptPdfData(this.receipt.uuid, this.inventorySearchModel ? this.inventorySearchModel : {});
        EventBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
          ...response.body,
          filename: `Receipt ${this.receipt.receipt_no}`
        })
      },
      async onReceiptStickerClick() {
        EventBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Receipt Sticker')});
        let response = await ReceiptService.getReceiptSticker(this.receipt.uuid);
        EventBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
          ...response.body,
          filename: `Receipt Sticker ${this.receipt.receipt_no}`
        })
      },
      initBreadcrumbs() {
        this.breadcrumbs = [
          {
            text: this.$i18n.t('Receipts'),
            to: {name: 'receipts'},
            icon: 'receipt'
          },
          {
            text: `${this.receipt.receipt_no}`,
          }
        ];
      },
      onGenerateReceiptContractClick() {
        this.getGeneralContractModalData({
          object: this.receipt,
          fileType: 'receipt_contract',
          objectName: 'receipt',
        });
        this.showGeneralContractModal({
          source: SOURCE_RECEIPT_VIEW,
          object: this.receipt,
          fileType: 'receipt_contract',
          objectName: 'receipt',
          enableESignature: true,
          refreshOnSubmit: true,
          modalTitle: this.$i18n.t("Receipt Consignment")
        })
      },
      onGeneratePreReceiptContractClick() {
        this.getGeneralContractModalData({
          object: this.receipt,
          fileType: 'pre_receipt_contract',
          objectName: 'receipt',
        });
        this.showGeneralContractModal({
          source: SOURCE_RECEIPT_VIEW,
          object: this.receipt,
          fileType: 'pre_receipt_contract',
          objectName: 'receipt',
          enableESignature: true,
          refreshOnSubmit: true,
          modalTitle: this.$i18n.t("Receipt Pre-Consignment")
        })
      }

    },
    async beforeMount() {
      const success = await this.getReceipt(this.$route.params.id);
      if (!success) {
        await this.$errorDialog(this.$i18n.t('Something went wrong'));
        this.$generalGoBack(this)
      } else {
        this.initBreadcrumbs();
      }
    },
    destroyed() {
      this.updateViewReceipt(null)
    }
  }

</script>

<style lang="scss">
  /*@import "../scss/variables";

  .receipt-view {
    @include view-page-wrapper;
  }*/
</style>
